import { useEffect, useRef, useState } from "react";
import { Play, Pause, Music2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const tracks = [
  { id: "homage", title: "Homage", src: "/assets/homage-fiya-red.mp3" },
  { id: "in-the-pink-rain", title: "In the Pink Rain", src: "/assets/in-the-pink-rain-fiya-red.mp3" },
];

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const Listen = () => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    const t = tracks.find((x) => x.id === current);
    audio.src = t.src;
    audio.play().catch(() => {});
  }, [current]);

  const toggle = (id) => {
    const audio = audioRef.current;
    if (current === id) {
      if (playing) audio.pause();
      else audio.play().catch(() => {});
    } else {
      setCurrent(id);
      setTime(0);
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <section id="listen" data-testid="listen-section" className="bg-[#2D1A13] text-[#FDFBF7] py-28 md:py-44 overflow-hidden">
      <audio
        ref={audioRef}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#FDFBF7]/50 mb-6">Fresh Scoops</p>
            <h2 className="font-serif-display font-bold tracking-tighter text-4xl md:text-6xl leading-tight">
              Press play on <span className="italic text-[#FF8E9E]">Fiya Red.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm font-light leading-relaxed text-[#FDFBF7]/60">
            Latest releases straight from the studio. Headphones recommended, volume up.
          </p>
        </Reveal>

        <div data-testid="track-list" className="border-b border-[#FDFBF7]/15">
          {tracks.map((t, i) => {
            const active = current === t.id;
            const isPlaying = active && playing;
            return (
              <Reveal key={t.id} delay={i * 0.08} y={20}>
                <div
                  data-testid={`track-row-${t.id}`}
                  className={`group border-t border-[#FDFBF7]/15 py-8 md:py-10 px-2 md:px-6 transition-colors duration-500 ${active ? "bg-[#FDFBF7]/5" : "hover:bg-[#FDFBF7]/[0.03]"}`}
                >
                  <div className="flex items-center gap-6 md:gap-10">
                    <span className="font-serif-display italic text-lg text-[#FDFBF7]/35 w-8">0{i + 1}</span>
                    <button
                      data-testid={`track-play-${t.id}`}
                      onClick={() => toggle(t.id)}
                      aria-label={isPlaying ? `Pause ${t.title}` : `Play ${t.title}`}
                      className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-colors duration-300 ${
                        isPlaying
                          ? "bg-[#FF8E9E] text-[#2D1A13]"
                          : "border border-[#FDFBF7]/30 text-[#FDFBF7] hover:bg-[#FF8E9E] hover:border-[#FF8E9E] hover:text-[#2D1A13]"
                      }`}
                    >
                      {isPlaying ? <Pause size={22} strokeWidth={1.8} /> : <Play size={22} strokeWidth={1.8} className="ml-1" />}
                    </button>
                    <div
                      data-testid={`track-art-${t.id}`}
                      className={`hidden md:flex w-24 h-24 rounded-full shrink-0 bg-[#F5EBDC] shadow-[0_10px_30px_rgba(0,0,0,0.25)] items-center justify-center ${isPlaying ? "spin-slow" : ""}`}
                    >
                      <span className="font-serif-display italic font-bold text-[13px] text-[#2D1A13] leading-tight text-center px-3">
                        {t.title}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif-display font-bold text-3xl md:text-5xl tracking-tight truncate group-hover:translate-x-2 transition-transform duration-500">
                        {t.title}
                      </h3>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#DDA77B] mt-2 flex items-center gap-2">
                        <Music2 size={12} strokeWidth={1.8} /> Fiya Red · 30s snippet
                      </p>
                    </div>
                    <div className="hidden sm:flex items-end gap-1 h-8 w-10">
                      {[0, 1, 2, 3].map((b) => (
                        <span
                          key={b}
                          className={`w-1.5 rounded-full bg-[#FF8E9E] ${isPlaying ? "eq-bar" : "scale-y-[0.25]"}`}
                          style={{ height: "100%", animationDelay: `${b * 0.15}s`, transformOrigin: "bottom" }}
                        />
                      ))}
                    </div>
                    <span data-testid={`track-time-${t.id}`} className="text-xs font-semibold tracking-[0.15em] text-[#FDFBF7]/50 tabular-nums w-24 text-right">
                      {active ? `${fmt(time)} / ${fmt(duration)}` : "—"}
                    </span>
                  </div>
                  <div
                    data-testid={`track-progress-${t.id}`}
                    onClick={active ? seek : undefined}
                    className={`mt-6 h-[3px] rounded-full bg-[#FDFBF7]/10 overflow-hidden ${active ? "cursor-pointer" : ""}`}
                  >
                    <div
                      className="h-full bg-[#FF8E9E] rounded-full transition-[width] duration-200"
                      style={{ width: active && duration ? `${(time / duration) * 100}%` : "0%" }}
                    />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
